import { Trans, t } from '@lingui/macro';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  Activity,
  Delete,
  FileText,
  Forward,
  GripHorizontal,
  Maximize2,
  Mic,
  MicOff,
  MoreHorizontal,
  Minus,
  Pause,
  Phone,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
  Play,
  PauseCircle,
  Search,
  Volume2,
  X,
} from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@shared/api/supabaseClient';
import type {
  ActiveCallResponse,
  CallCenterCallLog,
  CallCenterContactDestination,
  CallCenterIncomingCallRealtimeEvent,
} from '@jema-manager/shared/types/messaging.types';
import { Button } from '@shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/ui/dialog';
import { Input } from '@shared/ui/input';
import { messagingApi } from '@shared/api/messaging';
import { useAuth } from '@shared/hooks/useAuth';
import { logger } from '@shared/lib/logger';
import {
  callsQueryKeys,
  useActiveCalls,
  useCallCenterDeviceHeartbeat,
  useCallCenterContacts,
  useCallQualitySummary,
  useClaimIncomingCall,
  useControlCallRecording,
  useControlCallTranscription,
  useEndCallCenterCall,
  useTransferCallCenterCall,
} from '../hooks/useCallsApi';
import { useCallAvailability } from '../lib/callAvailability';
import { getCallDeviceId } from '../lib/deviceId';
import { useIncomingCallRingtone, usePrimeIncomingCallRingtone } from '../lib/incomingCallRingtone';
import {
  CallDeviceNotConnectedError,
  callWebRtcEndedEventName,
  callWebRtcIncomingEventName,
  useTelnyxWebRtcCall,
  type CallWebRtcEndedDetail,
} from '../lib/telnyxWebRtcClient';

const transferRegionDefault = 'US' as const;

/**
 * Normalizes operator-entered transfer targets to E.164 using the US default
 * region (matching the backend `normalizePhoneNumber`). Returns `null` for
 * input that is not a valid phone number so the dialog can block the transfer.
 */
export const normalizeTransferNumber = (
  raw: string
): { e164: string; formatted: string } | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = parsePhoneNumberFromString(trimmed, transferRegionDefault);
  // Gate on possibility (length/format) rather than strict validity so dialable
  // targets the operator types — including ones the strict metadata flags as
  // non-geographic — still transfer; the backend re-normalizes on transfer.
  if (!parsed?.isPossible()) return null;
  return { e164: parsed.number, formatted: parsed.formatInternational() };
};

const dismissedCallsKey = 'jema.calls.dismissed-ringing';
const dockPositionKey = 'jema.calls.global-dock-position';
const keypadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', 'backspace'];

function useCallDuration(isoTimestamp: string | null | undefined): string | null {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    if (!isoTimestamp) return;
    const base = new Date(isoTimestamp).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - base) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isoTimestamp]);
  if (!isoTimestamp) return null;
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const getOutboundPhaseLabel = (status: string): string | null => {
  if (status === 'initiated')
    return t({ id: 'features.calls.global.calling', message: 'Calling…' });
  if (status === 'ringing')
    return t({ id: 'features.calls.global.outboundRinging', message: 'Ringing…' });
  return null;
};

// Poll cadence: the WebRTC invite event covers instant incoming-call delivery,
// so steady-state polling can stay slow without hurting answer latency.
const RINGING_POLL_INTERVAL_MS = 3_000;
const ACTIVE_CALL_POLL_INTERVAL_MS = 5_000;
const IDLE_POLL_INTERVAL_MS = 10_000;
const CALL_CENTER_STAFF_CHANNEL = 'calls:staff';
const CALL_CENTER_USER_CHANNEL_PREFIX = 'calls:user';
const CALL_CENTER_STATE_CHANGED_EVENT = 'call-state-changed';
const CALL_CENTER_INCOMING_CALL_EVENT = 'incoming-call';

/**
 * Minimal ringing entry built from the backend `incoming_call` broadcast so
 * the dock renders instantly; the next active-calls refetch replaces it with
 * the full server record.
 */
const toOptimisticRingingCall = (
  event: CallCenterIncomingCallRealtimeEvent
): CallCenterCallLog => ({
  id: event.callId,
  roomId: null,
  patientId: event.patientId ?? null,
  doctorId: null,
  quoteId: null,
  serviceRequestId: null,
  travelCaseId: null,
  provider: 'telnyx_call_control',
  direction: 'inbound',
  fromNumber: event.fromNumber,
  toNumber: event.toNumber,
  status: 'ringing',
  callCenterState: 'ringing',
  failureReason: null,
  startedAt: event.startedAt,
  endedAt: null,
  createdAt: event.startedAt,
  updatedAt: event.startedAt,
  contact: event.callerName ? { id: event.callId, name: event.callerName, role: 'caller' } : null,
});

type DockPosition = { x: number; y: number };

const readDismissedCallIds = () => {
  if (typeof window === 'undefined') return new Set<string>();
  try {
    return new Set(JSON.parse(window.localStorage.getItem(dismissedCallsKey) ?? '[]') as string[]);
  } catch {
    return new Set<string>();
  }
};

const writeDismissedCallIds = (ids: Set<string>) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(dismissedCallsKey, JSON.stringify([...ids].slice(-50)));
};

const readDockPosition = (): DockPosition | null => {
  if (typeof window === 'undefined') return null;
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(dockPositionKey) ?? 'null'
    ) as DockPosition | null;
    if (!parsed || typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const clampDockPosition = (position: DockPosition, width = 420, height = 360): DockPosition => {
  if (typeof window === 'undefined') return position;
  const margin = 12;
  return {
    x: Math.min(Math.max(margin, position.x), Math.max(margin, window.innerWidth - width - margin)),
    y: Math.min(
      Math.max(margin, position.y),
      Math.max(margin, window.innerHeight - height - margin)
    ),
  };
};

const isOwnActiveCall = (call: CallCenterCallLog, userId?: string | null) =>
  Boolean(
    userId &&
      (call.answeredByUserId === userId || call.initiatedByUserId === userId) &&
      (call.status === 'answered' || call.status === 'initiated' || call.status === 'held')
  );

const isHeldCall = (call: CallCenterCallLog) =>
  Boolean(call.callCenterState === 'held' || call.heldAt);
const isRinging = (call: CallCenterCallLog) =>
  call.direction === 'inbound' && call.status === 'ringing';
const getPrimaryNumber = (call: CallCenterCallLog) =>
  call.direction === 'inbound' ? call.fromNumber : call.toNumber;
const getTransferTargetNumber = (call: CallCenterCallLog) =>
  call.transferTargetNumber?.trim() ||
  (typeof call.metadata?.transferTargetNumberE164 === 'string'
    ? call.metadata.transferTargetNumberE164.trim()
    : null);
const getCallName = (call: CallCenterCallLog) =>
  call.contact?.name ||
  getPrimaryNumber(call) ||
  t({ id: 'features.calls.global.unknown', message: 'Unknown caller' });

export function GlobalCallAlert() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isCallStaff = user?.role === 'admin' || user?.role === 'super_admin';
  const { isOnline } = useCallAvailability();
  // The backend learns call state from Telnyx webhooks and the browser gets a
  // push signal (the WebRTC invite) for incoming calls, so polling only fills
  // the gaps: fast while a call is ringing or live, slow when idle. The hook
  // additionally slows hidden tabs and backs off hard while rate-limited.
  const [pollIntervalMs, setPollIntervalMs] = React.useState(IDLE_POLL_INTERVAL_MS);
  const activeCalls = useActiveCalls({
    enabled: Boolean(isCallStaff),
    refetchInterval: isOnline ? pollIntervalMs : false,
    refetchIntervalInBackground: isOnline,
  });
  const claimCall = useClaimIncomingCall();
  const endCall = useEndCallCenterCall();
  const transferCall = useTransferCallCenterCall();
  const recordingControl = useControlCallRecording();
  const transcriptionControl = useControlCallTranscription();
  const { mutate: sendDeviceHeartbeat } = useCallCenterDeviceHeartbeat();
  const browserCall = useTelnyxWebRtcCall();
  const { clientStatus } = browserCall;
  const [dismissedCallIds, setDismissedCallIds] = React.useState<Set<string>>(() =>
    readDismissedCallIds()
  );
  const [isAnswering, setIsAnswering] = React.useState(false);
  const [callActionId, setCallActionId] = React.useState<string | null>(null);
  const [alertMessage, setAlertMessage] = React.useState<string | null>(null);
  const [minimized, setMinimized] = React.useState(false);
  const [selectedCallId, setSelectedCallId] = React.useState<string | null>(null);
  const [advancedControlsOpen, setAdvancedControlsOpen] = React.useState(false);
  const [transferTarget, setTransferTarget] = React.useState<CallCenterCallLog | null>(null);
  const [recordingPausedIds, setRecordingPausedIds] = React.useState<Set<string>>(() => new Set());
  const [transcribingIds, setTranscribingIds] = React.useState<Set<string>>(() => new Set());
  const [dockPosition, setDockPosition] = React.useState<DockPosition | null>(() =>
    readDockPosition()
  );
  const dragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origin: DockPosition;
  } | null>(null);
  const answerInFlightRef = React.useRef(false);
  const switchInFlightRef = React.useRef(false);
  const autoEndedCallIds = React.useRef(new Set<string>());
  const callsRef = React.useRef<CallCenterCallLog[]>([]);

  usePrimeIncomingCallRingtone(Boolean(isCallStaff && isOnline));

  // Surface WebRTC connection/call failures as a toast. Benign transitions
  // (normal call end, reconnects) clear errorMessage to null, so this only
  // fires on real SDK errors; the ref dedupes re-renders of the same error.
  const lastWebRtcErrorRef = React.useRef<string | null>(null);
  const webRtcErrorMessage = browserCall.errorMessage ?? null;
  React.useEffect(() => {
    if (!isCallStaff) return;
    if (!webRtcErrorMessage) {
      lastWebRtcErrorRef.current = null;
      return;
    }
    if (lastWebRtcErrorRef.current === webRtcErrorMessage) return;
    lastWebRtcErrorRef.current = webRtcErrorMessage;
    toast.error(
      t({
        id: 'features.calls.errors.webrtcConnection',
        message: 'Call connection problem — check your network and try again.',
      })
    );
  }, [isCallStaff, webRtcErrorMessage]);

  React.useEffect(() => {
    const activeIds = new Set((activeCalls.data?.calls ?? []).map(call => call.id));
    // Drop auto-end guards for calls that have left the active list so the set
    // does not grow unbounded over the dock's lifetime.
    autoEndedCallIds.current.forEach(id => {
      if (!activeIds.has(id)) autoEndedCallIds.current.delete(id);
    });
    if (!isOnline || !activeCalls.data?.calls.length) return;
    setDismissedCallIds(previous => {
      const next = new Set([...previous].filter(id => activeIds.has(id)));
      if (next.size !== previous.size) writeDismissedCallIds(next);
      return next;
    });
  }, [activeCalls.data?.calls, isOnline]);

  const calls = activeCalls.data?.calls ?? [];
  callsRef.current = calls;
  const ownCalls = calls.filter(call => isOwnActiveCall(call, user?.id));
  const ringingCalls = isOnline
    ? calls.filter(call => isRinging(call) && !dismissedCallIds.has(call.id))
    : [];
  const visibleCalls = [
    ...ownCalls,
    ...ringingCalls.filter(call => !ownCalls.some(c => c.id === call.id)),
  ];
  const selectedCall =
    visibleCalls.find(call => call.id === selectedCallId) ?? visibleCalls[0] ?? null;
  const ringingCall = ringingCalls[0] ?? null;
  const selectedIsOwnCall = selectedCall ? isOwnActiveCall(selectedCall, user?.id) : false;
  const selectedQuality = useCallQualitySummary(
    selectedCall?.id ?? null,
    Boolean(isCallStaff && selectedCall)
  );

  useIncomingCallRingtone(Boolean(ringingCalls.length > 0), ringingCall?.id);

  const hasRingingCall = ringingCalls.length > 0;
  const hasOwnCall = ownCalls.length > 0;
  React.useEffect(() => {
    setPollIntervalMs(
      hasRingingCall
        ? RINGING_POLL_INTERVAL_MS
        : hasOwnCall
          ? ACTIVE_CALL_POLL_INTERVAL_MS
          : IDLE_POLL_INTERVAL_MS
    );
  }, [hasRingingCall, hasOwnCall]);

  React.useEffect(() => {
    if (!isCallStaff) return undefined;
    const sendHeartbeat = () => {
      sendDeviceHeartbeat({
        deviceId: getCallDeviceId(),
        platform: 'web',
        callingStatus: isOnline ? (ownCalls.length > 0 ? 'busy' : 'ready') : 'offline',
        activeCallCount: ownCalls.length,
        appVersion: 'manager-web',
        metadata: {
          clientStatus,
          callStatus: browserCall.callStatus,
          visibilityState: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
        },
      });
    };
    sendHeartbeat();
    const timer = window.setInterval(sendHeartbeat, 30_000);
    return () => window.clearInterval(timer);
  }, [browserCall.callStatus, clientStatus, isCallStaff, ownCalls.length, sendDeviceHeartbeat]);

  // Push signal: when a Telnyx WebRTC invite reaches this browser, backend call
  // state has changed — refetch immediately rather than waiting out the idle
  // poll interval.
  const refetchActiveCalls = activeCalls.refetch;
  React.useEffect(() => {
    if (!isCallStaff) return undefined;
    const handleIncoming = () => {
      void refetchActiveCalls();
    };
    window.addEventListener(callWebRtcIncomingEventName, handleIncoming);
    return () => window.removeEventListener(callWebRtcIncomingEventName, handleIncoming);
  }, [isCallStaff, refetchActiveCalls]);

  React.useEffect(() => {
    if (!isCallStaff || !isOnline) return undefined;
    if (!user?.id) return undefined;
    const staffChannel = supabase.channel(CALL_CENTER_STAFF_CHANNEL, {
      config: { private: true, broadcast: { ack: false } },
    });
    staffChannel
      .on('broadcast', { event: CALL_CENTER_STATE_CHANGED_EVENT }, () => {
        void refetchActiveCalls();
      })
      .subscribe();

    const incomingChannel = supabase.channel(`${CALL_CENTER_USER_CHANNEL_PREFIX}:${user.id}`, {
      config: { private: true, broadcast: { ack: false } },
    });
    incomingChannel
      .on('broadcast', { event: CALL_CENTER_INCOMING_CALL_EVENT }, message => {
        // Backend-authoritative instant ring: seed the dock from the event
        // payload, then reconcile against the server list.
        const event = message.payload as CallCenterIncomingCallRealtimeEvent | undefined;
        if (event?.type === 'incoming_call' && event.callId) {
          queryClient.setQueryData<ActiveCallResponse>(callsQueryKeys.active(), previous => {
            const calls = previous?.calls ?? [];
            if (calls.some(call => call.id === event.callId)) return previous;
            return { ...previous, calls: [toOptimisticRingingCall(event), ...calls] };
          });
        }
        void refetchActiveCalls();
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(staffChannel);
      void supabase.removeChannel(incomingChannel);
    };
  }, [isCallStaff, isOnline, queryClient, refetchActiveCalls, user?.id]);

  React.useEffect(() => {
    if (!selectedCall && visibleCalls[0]) {
      setSelectedCallId(visibleCalls[0].id);
    }
  }, [selectedCall, visibleCalls]);

  React.useEffect(() => {
    setAdvancedControlsOpen(false);
  }, [selectedCall?.id]);

  // End the matching backend call when a specific WebRTC leg terminates (remote
  // hang-up). Keyed off the ended leg's logical call id so one ending leg never
  // ends the other concurrent calls.
  React.useEffect(() => {
    if (!isCallStaff) return undefined;
    const handleEnded = (event: Event) => {
      const detail = (event as CustomEvent<CallWebRtcEndedDetail>).detail;
      const logicalCallId = detail?.logicalCallId;
      if (!logicalCallId || autoEndedCallIds.current.has(logicalCallId)) return;
      // Show "no answer" / "call failed" toast for outbound calls that never connected.
      const endedCall = callsRef.current.find(c => c.id === logicalCallId);
      if (endedCall?.direction === 'outbound') {
        if (endedCall.status === 'initiated' || endedCall.status === 'ringing') {
          toast.error(t({ id: 'features.calls.noAnswer', message: 'No answer.' }));
        } else if (endedCall.status === 'failed') {
          toast.error(
            endedCall.failureReason ??
              t({ id: 'features.calls.callFailed', message: 'Call failed.' })
          );
        }
      }
      autoEndedCallIds.current.add(logicalCallId);
      void endCall.mutateAsync(logicalCallId).catch(() => {
        autoEndedCallIds.current.delete(logicalCallId);
      });
    };
    window.addEventListener(callWebRtcEndedEventName, handleEnded);
    return () => window.removeEventListener(callWebRtcEndedEventName, handleEnded);
  }, [endCall, isCallStaff]);

  React.useEffect(() => {
    if (!isCallStaff || !activeCalls.data) return;
    const activeCallIds = new Set(activeCalls.data.calls.map(call => call.id));
    const staleBrowserLines = browserCall.callLines.filter(
      line => line.logicalCallId && !activeCallIds.has(line.logicalCallId)
    );
    staleBrowserLines.forEach(line => {
      const logicalCallId = line.logicalCallId;
      if (!logicalCallId || autoEndedCallIds.current.has(logicalCallId)) return;
      autoEndedCallIds.current.add(logicalCallId);
      void browserCall
        .hangup(logicalCallId)
        .catch(error => {
          logger.error(
            'Stale browser call cleanup failed',
            error instanceof Error ? error : new Error(String(error)),
            { callId: logicalCallId, category: 'calls_webrtc_cleanup' }
          );
        })
        .finally(() => {
          autoEndedCallIds.current.delete(logicalCallId);
        });
    });
  }, [activeCalls.data, browserCall, isCallStaff]);

  if (!isCallStaff || !selectedCall) return null;

  const lineHeld = (call: CallCenterCallLog) => {
    const line = browserCall.getLineByLogicalId(call.id);
    if (line) return line.status === 'held';
    return isHeldCall(call);
  };
  const lineIsLive = (call: CallCenterCallLog) => {
    const line = browserCall.getLineByLogicalId(call.id);
    return Boolean(line?.isLive && line.status !== 'held');
  };

  const isOwnCall = selectedIsOwnCall;
  const isHeldOwnCall = isOwnCall && lineHeld(selectedCall);
  const isRecordingPaused = recordingPausedIds.has(selectedCall.id);
  const isTranscribing = transcribingIds.has(selectedCall.id);
  const selectedLineMuted = browserCall.getLineByLogicalId(selectedCall.id)?.isMuted ?? false;
  const isActionPending =
    callActionId === selectedCall.id ||
    recordingControl.isPending ||
    transcriptionControl.isPending;
  const canTransferSelectedCall = Boolean(selectedCall.providerCallControlId);
  const style = dockPosition
    ? ({ left: dockPosition.x, top: dockPosition.y } as React.CSSProperties)
    : undefined;

  const handleDragStart = (event: React.PointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.closest('aside')?.getBoundingClientRect();
    const origin = dockPosition ?? {
      x: rect?.left ?? window.innerWidth - 440,
      y: rect?.top ?? window.innerHeight - 420,
    };
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = clampDockPosition({
      x: drag.origin.x + event.clientX - drag.startX,
      y: drag.origin.y + event.clientY - drag.startY,
    });
    setDockPosition(next);
  };

  const handleDragEnd = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (dockPosition) {
      window.localStorage.setItem(dockPositionKey, JSON.stringify(dockPosition));
    }
  };

  const handleDismiss = (call: CallCenterCallLog) => {
    setDismissedCallIds(previous => {
      const next = new Set(previous).add(call.id);
      writeDismissedCallIds(next);
      return next;
    });
  };

  const handleClaim = async (call: CallCenterCallLog) => {
    if (answerInFlightRef.current) return;
    answerInFlightRef.current = true;
    setSelectedCallId(call.id);
    setIsAnswering(true);
    try {
      await claimCall.mutateAsync(call.id);
      toast.success(t({ id: 'features.calls.claimed', message: 'Call answered.' }));
    } catch (error) {
      const isConflict =
        typeof error === 'object' &&
        error !== null &&
        (error as { status?: number }).status === 409;
      const errorMessage = error instanceof Error ? error.message : null;
      const isDeviceNotConnected = error instanceof CallDeviceNotConnectedError;
      const isMissingBrowserCall =
        errorMessage?.startsWith('No Telnyx WebRTC invite reached this browser') ||
        errorMessage === 'No incoming browser call is available to answer.';
      logger.error(
        'Incoming call answer failed',
        error instanceof Error ? error : new Error(String(error)),
        {
          callId: call.id,
          direction: call.direction,
          backendStatus: call.status,
          provider: call.provider,
          browserClientStatus: browserCall.clientStatus,
          browserCallStatus: browserCall.callStatus,
          browserPermissionState: browserCall.permissionState,
          hasActiveBrowserCall: Boolean(browserCall.activeCall),
          activeBrowserCallId: browserCall.activeCall?.id ?? null,
          isConflict,
          isDeviceNotConnected,
          isMissingBrowserCall,
          category: 'calls_webrtc_answer',
        }
      );
      toast.error(
        isConflict
          ? t({
              id: 'features.calls.errors.alreadyAnswered',
              message: 'This call was already answered.',
            })
          : isDeviceNotConnected
            ? t({
                id: 'features.calls.errors.deviceNotConnected',
                message:
                  'This device is not connected to the calling service. Check your network or VPN and reload, then try again.',
              })
            : isMissingBrowserCall
              ? t({
                  id: 'features.calls.errors.browserCallConnecting',
                  message: 'This browser is still connecting to the call. Try answering again.',
                })
              : t({
                  id: 'features.calls.errors.answerFailed',
                  message:
                    'Call could not be answered. Check your browser audio setup and try again.',
                })
      );
    } finally {
      answerInFlightRef.current = false;
      setIsAnswering(false);
    }
  };

  const handleHold = async (call: CallCenterCallLog) => {
    setAlertMessage(null);
    setCallActionId(call.id);
    try {
      const response = await messagingApi.holdCallCenterCall(call.id);
      await activeCalls.refetch();
      if (
        response.warning?.code === 'hold_audio_failed' ||
        response.warning?.code === 'hold_audio_unavailable'
      ) {
        const message =
          response.warning.code === 'hold_audio_unavailable'
            ? t({
                id: 'features.calls.errors.holdAudioUnavailable',
                message: 'Hold audio unavailable',
              })
            : t({
                id: 'features.calls.errors.holdAudioFailed',
                message: 'Hold audio failed',
              });
        setAlertMessage(message);
        toast.error(message);
      } else {
        toast.success(t({ id: 'features.calls.held', message: 'Call held.' }));
      }
    } catch (error) {
      logger.error('Call hold failed', error instanceof Error ? error : new Error(String(error)), {
        callId: call.id,
        category: 'calls_webrtc_hold',
      });
      const message = t({
        id: 'features.calls.errors.holdFailed',
        message: 'Call could not be placed on hold. Try again.',
      });
      setAlertMessage(message);
      toast.error(message);
    } finally {
      setCallActionId(null);
    }
  };

  const handleResume = async (call: CallCenterCallLog) => {
    setAlertMessage(null);
    setCallActionId(call.id);
    try {
      await messagingApi.resumeCallCenterCall(call.id);
      await activeCalls.refetch();
      toast.success(t({ id: 'features.calls.resumed', message: 'Call resumed.' }));
    } catch (error) {
      logger.error(
        'Call resume failed',
        error instanceof Error ? error : new Error(String(error)),
        {
          callId: call.id,
          category: 'calls_webrtc_resume',
        }
      );
      const message = t({
        id: 'features.calls.errors.resumeFailed',
        message: 'Call could not be resumed. Try again.',
      });
      setAlertMessage(message);
      toast.error(message);
    } finally {
      setCallActionId(null);
    }
  };

  const handleEnd = async (call: CallCenterCallLog) => {
    setCallActionId(call.id);
    // Claim the auto-end guard up front: hangup() dispatches the leg-ended event
    // that the listener above also reacts to, so this prevents a double end.
    autoEndedCallIds.current.add(call.id);
    try {
      await browserCall.hangup(call.id);
    } catch {
      // The SDK may already have torn this leg down. Backend cleanup still owns UI truth.
    }
    try {
      await endCall.mutateAsync(call.id);
      await activeCalls.refetch();
    } catch {
      // Release the auto-end guard so the leg-ended listener can retry cleanup.
      autoEndedCallIds.current.delete(call.id);
      toast.error(
        t({ id: 'features.calls.errors.endFailed', message: 'Call could not be ended. Try again.' })
      );
    } finally {
      setCallActionId(null);
    }
  };

  // Selecting another answered call makes it the live line: the SDK holds the
  // previously-live leg and unholds the target, and the backend hold/resume
  // endpoints keep the parked caller on hold audio while the operator talks.
  const handleSelectCall = async (call: CallCenterCallLog) => {
    setSelectedCallId(call.id);
    if (!isHeldCall(call)) return;
    // Guard against rapid re-entrant switching: interleaved SDK + backend
    // hold/resume calls could otherwise leave two lines live or hold the wrong
    // leg.
    if (switchInFlightRef.current) return;
    switchInFlightRef.current = true;
    const previousLive = visibleCalls.find(other => other.id !== call.id && lineIsLive(other));
    setCallActionId(call.id);
    try {
      if (previousLive) {
        await messagingApi.holdCallCenterCall(previousLive.id);
      }
      await messagingApi.resumeCallCenterCall(call.id);
      await activeCalls.refetch();
    } catch (error) {
      logger.error(
        'Call switch failed',
        error instanceof Error ? error : new Error(String(error)),
        { callId: call.id, category: 'calls_webrtc_switch' }
      );
      toast.error(
        t({ id: 'features.calls.errors.switchFailed', message: 'Could not switch to that call.' })
      );
    } finally {
      switchInFlightRef.current = false;
      setCallActionId(null);
    }
  };

  const handleTransfer = async (toNumber: string) => {
    if (!transferTarget) return;
    try {
      await transferCall.mutateAsync({ callLogId: transferTarget.id, input: { toNumber } });
      await activeCalls.refetch();
      toast.success(t({ id: 'features.calls.transferStarted', message: 'Transfer started.' }));
      setTransferTarget(null);
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message
          : t({
              id: 'features.calls.errors.transferFailed',
              message: 'Call could not be transferred.',
            });
      setAlertMessage(message);
      toast.error(message);
    }
  };

  const handleRecordingPrivacy = async (call: CallCenterCallLog) => {
    const action = recordingPausedIds.has(call.id) ? 'resume' : 'pause';
    setCallActionId(call.id);
    try {
      await recordingControl.mutateAsync({
        callLogId: call.id,
        input: {
          action,
          reason: action === 'pause' ? 'operator_privacy_pause' : 'operator_privacy_resume',
        },
      });
      setRecordingPausedIds(previous => {
        const next = new Set(previous);
        if (action === 'pause') next.add(call.id);
        else next.delete(call.id);
        return next;
      });
      toast.success(
        action === 'pause'
          ? t({ id: 'features.calls.recordingPaused', message: 'Recording paused.' })
          : t({ id: 'features.calls.recordingResumed', message: 'Recording resumed.' })
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message
          : t({
              id: 'features.calls.errors.recordingControlFailed',
              message: 'Recording control failed.',
            });
      setAlertMessage(message);
      toast.error(message);
    } finally {
      setCallActionId(null);
    }
  };

  const handleTranscriptionControl = async (call: CallCenterCallLog) => {
    const action = transcribingIds.has(call.id) ? 'stop' : 'start';
    setCallActionId(call.id);
    try {
      await transcriptionControl.mutateAsync({
        callLogId: call.id,
        input: {
          action,
          reason: action === 'start' ? 'operator_started' : 'operator_stopped',
        },
      });
      setTranscribingIds(previous => {
        const next = new Set(previous);
        if (action === 'start') next.add(call.id);
        else next.delete(call.id);
        return next;
      });
      toast.success(
        action === 'start'
          ? t({ id: 'features.calls.transcriptionStarted', message: 'Transcription started.' })
          : t({ id: 'features.calls.transcriptionStopped', message: 'Transcription stopped.' })
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message
          : t({
              id: 'features.calls.errors.transcriptionControlFailed',
              message: 'Transcription control failed.',
            });
      setAlertMessage(message);
      toast.error(message);
    } finally {
      setCallActionId(null);
    }
  };

  return (
    <>
      <aside
        aria-live='assertive'
        style={style}
        className={
          dockPosition
            ? 'fixed z-[70] w-[min(430px,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-border bg-background shadow-2xl shadow-black/20'
            : 'fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-3 right-3 z-[70] overflow-hidden rounded-2xl border border-border bg-background shadow-2xl shadow-black/20 sm:bottom-5 sm:left-auto sm:right-5 sm:w-[min(430px,calc(100vw-2rem))]'
        }
      >
        <div
          className='flex touch-none items-center justify-between gap-2 border-b border-border bg-muted/35 px-3 py-2'
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
        >
          <div className='flex min-w-0 items-center gap-2'>
            <GripHorizontal className='h-4 w-4 shrink-0 text-muted-foreground' aria-hidden='true' />
            <span className='truncate text-xs font-semibold uppercase text-muted-foreground'>
              <Trans id='features.calls.global.callDock'>Call dock</Trans>
            </span>
            {visibleCalls.length > 1 ? (
              <span className='rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-bold text-brand-blue'>
                {visibleCalls.length}
              </span>
            ) : null}
          </div>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-8 w-8'
            onClick={() => setMinimized(previous => !previous)}
            aria-label={
              minimized
                ? t({ id: 'features.calls.global.expand', message: 'Expand call dock' })
                : t({ id: 'features.calls.global.minimize', message: 'Minimize call dock' })
            }
          >
            {minimized ? <Maximize2 className='h-4 w-4' /> : <Minus className='h-4 w-4' />}
          </Button>
        </div>

        {minimized ? (
          <button
            type='button'
            className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left'
            onClick={() => setMinimized(false)}
          >
            <CallIdentity
              call={selectedCall}
              isOwnCall={isOwnCall}
              isHeld={Boolean(isHeldOwnCall)}
            />
            <span className='text-xs font-semibold text-muted-foreground'>
              {browserCall.callStatus}
            </span>
          </button>
        ) : (
          <div className='grid max-h-[min(78vh,620px)] gap-3 overflow-y-auto p-3'>
            {alertMessage ? (
              <p
                role='alert'
                className='rounded-lg bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive'
              >
                {alertMessage}
              </p>
            ) : null}

            <div className='grid gap-2'>
              {visibleCalls.map(call => (
                <CallRow
                  key={call.id}
                  call={call}
                  selected={call.id === selectedCall.id}
                  isOwnCall={isOwnActiveCall(call, user?.id)}
                  isHeld={lineHeld(call)}
                  onSelect={() => void handleSelectCall(call)}
                  onDismiss={() => handleDismiss(call)}
                />
              ))}
            </div>

            <div className='rounded-2xl border border-border bg-card p-3'>
              <CallIdentity
                call={selectedCall}
                isOwnCall={isOwnCall}
                isHeld={Boolean(isHeldOwnCall)}
              />
              {isOwnCall ? (
                <div className='mt-3 grid gap-3'>
                  <div className='flex flex-wrap items-center gap-2 rounded-xl border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground'>
                    <span className='inline-flex items-center gap-1 font-semibold text-foreground'>
                      <Activity className='h-3.5 w-3.5' aria-hidden='true' />
                      <Trans id='features.calls.quality'>Quality</Trans>
                    </span>
                    {selectedQuality.data?.sampleCount ? (
                      <>
                        <span>
                          <Trans id='features.calls.qualityMos'>MOS</Trans>{' '}
                          {selectedQuality.data.latestMos?.toFixed(1) ??
                            selectedQuality.data.minMos?.toFixed(1) ??
                            '—'}
                        </span>
                        {selectedQuality.data.warning ? (
                          <span className='rounded-full bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-700 dark:text-amber-200'>
                            <Trans id='features.calls.qualityWarning'>Low quality</Trans>
                          </span>
                        ) : (
                          <span className='rounded-full bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-700 dark:text-emerald-200'>
                            <Trans id='features.calls.qualityOk'>Stable</Trans>
                          </span>
                        )}
                      </>
                    ) : (
                      <span>
                        <Trans id='features.calls.qualityWaiting'>Waiting for samples</Trans>
                      </span>
                    )}
                    {isRecordingPaused ? (
                      <span className='rounded-full bg-rose-500/15 px-2 py-0.5 font-semibold text-rose-700 dark:text-rose-200'>
                        <Trans id='features.calls.recordingPrivacyActive'>Recording paused</Trans>
                      </span>
                    ) : null}
                    {isTranscribing ? (
                      <span className='rounded-full bg-brand-blue/10 px-2 py-0.5 font-semibold text-brand-blue'>
                        <Trans id='features.calls.transcribing'>Transcribing</Trans>
                      </span>
                    ) : null}
                  </div>
                  <div className='grid grid-cols-4 gap-2'>
                    <IconAction
                      label={
                        selectedLineMuted
                          ? t({ id: 'features.calls.unmute', message: 'Unmute' })
                          : t({ id: 'features.calls.mute', message: 'Mute' })
                      }
                      onClick={() =>
                        void (selectedLineMuted
                          ? browserCall.unmute(selectedCall.id)
                          : browserCall.mute(selectedCall.id))
                      }
                      disabled={isActionPending}
                    >
                      {selectedLineMuted ? (
                        <MicOff className='h-4 w-4' />
                      ) : (
                        <Mic className='h-4 w-4' />
                      )}
                    </IconAction>
                    <IconAction
                      label={
                        isHeldOwnCall
                          ? t({ id: 'features.calls.resume', message: 'Resume' })
                          : t({ id: 'features.calls.hold', message: 'Hold' })
                      }
                      onClick={() =>
                        void (isHeldOwnCall ? handleResume(selectedCall) : handleHold(selectedCall))
                      }
                      disabled={isActionPending}
                    >
                      {isHeldOwnCall ? <Play className='h-4 w-4' /> : <Pause className='h-4 w-4' />}
                    </IconAction>
                    <IconAction
                      label={t({ id: 'features.calls.moreControls', message: 'More' })}
                      onClick={() => setAdvancedControlsOpen(previous => !previous)}
                      disabled={isActionPending}
                      pressed={advancedControlsOpen}
                    >
                      <MoreHorizontal className='h-4 w-4' />
                    </IconAction>
                    <IconAction
                      label={t({ id: 'features.calls.hangUp', message: 'Hang up' })}
                      onClick={() => void handleEnd(selectedCall)}
                      disabled={isActionPending || endCall.isPending}
                      destructive
                    >
                      <PhoneOff className='h-4 w-4' />
                    </IconAction>
                  </div>
                  {advancedControlsOpen ? (
                    <div className='grid gap-2 rounded-xl border border-border bg-muted/15 p-2'>
                      {!isHeldOwnCall ? (
                        <GlobalCallDeviceControls browserCall={browserCall} />
                      ) : null}
                      <div className='grid grid-cols-3 gap-2'>
                        <IconAction
                          label={t({ id: 'features.calls.transfer', message: 'Transfer' })}
                          onClick={() => {
                            if (!canTransferSelectedCall) {
                              const message = t({
                                id: 'features.calls.errors.transferUnavailable',
                                message: 'Call cannot be transferred yet.',
                              });
                              setAlertMessage(message);
                              toast.error(message);
                              return;
                            }
                            setTransferTarget(selectedCall);
                          }}
                          disabled={isActionPending || transferCall.isPending}
                        >
                          <Forward className='h-4 w-4' />
                        </IconAction>
                        <IconAction
                          label={
                            isRecordingPaused
                              ? t({ id: 'features.calls.resumeRecording', message: 'Resume rec' })
                              : t({ id: 'features.calls.pauseRecording', message: 'Pause rec' })
                          }
                          onClick={() => void handleRecordingPrivacy(selectedCall)}
                          disabled={isActionPending}
                        >
                          <PauseCircle className='h-4 w-4' />
                        </IconAction>
                        <IconAction
                          label={
                            isTranscribing
                              ? t({ id: 'features.calls.stopTranscript', message: 'Stop text' })
                              : t({ id: 'features.calls.startTranscript', message: 'Start text' })
                          }
                          onClick={() => void handleTranscriptionControl(selectedCall)}
                          disabled={isActionPending}
                        >
                          <FileText className='h-4 w-4' />
                        </IconAction>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className='mt-3 grid grid-cols-2 gap-2'>
                  <Button
                    className='h-11 bg-emerald-600 text-white hover:bg-emerald-700'
                    onClick={() => void handleClaim(selectedCall)}
                    disabled={isAnswering || claimCall.isPending}
                  >
                    <Phone className='h-4 w-4' aria-hidden='true' />
                    <Trans id='features.calls.answer'>Answer</Trans>
                  </Button>
                  <Button
                    variant='outline'
                    className='h-11'
                    onClick={() => handleDismiss(selectedCall)}
                  >
                    <X className='h-4 w-4' aria-hidden='true' />
                    <Trans id='features.calls.global.dismiss'>Dismiss</Trans>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      <TransferCallDialog
        call={transferTarget}
        isPending={transferCall.isPending}
        onClose={() => setTransferTarget(null)}
        onTransfer={handleTransfer}
      />
    </>
  );
}

function CallIdentity({
  call,
  isOwnCall,
  isHeld,
}: {
  call: CallCenterCallLog;
  isOwnCall: boolean;
  isHeld: boolean;
}) {
  const transferTargetNumber = getTransferTargetNumber(call);
  const isOutboundDialing =
    isOwnCall && !isHeld && call.direction === 'outbound' && call.status !== 'answered';
  const outboundPhaseLabel = isOutboundDialing ? getOutboundPhaseLabel(call.status) : null;
  const duration = useCallDuration(
    isOwnCall && !isHeld && call.status === 'answered' ? call.lastStatusAt : null
  );

  let label: React.ReactNode;
  if (transferTargetNumber) {
    label = <Trans id='features.calls.global.transferred'>Transferred call</Trans>;
  } else if (isHeld) {
    label = <Trans id='features.calls.global.held'>Held call</Trans>;
  } else if (outboundPhaseLabel) {
    label = outboundPhaseLabel;
  } else if (isOwnCall) {
    label = <Trans id='features.calls.global.active'>Active call</Trans>;
  } else {
    label = <Trans id='features.calls.global.incoming'>Incoming call</Trans>;
  }

  return (
    <div className='flex min-w-0 items-center gap-3'>
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-lime/15 text-brand-lime ring-1 ring-brand-lime/25 ${isOutboundDialing ? 'animate-pulse' : ''}`}
      >
        {isOwnCall && call.direction === 'outbound' ? (
          <PhoneOutgoing className='h-5 w-5' />
        ) : isOwnCall ? (
          <Phone className='h-5 w-5' />
        ) : (
          <PhoneIncoming className='h-5 w-5' />
        )}
      </span>
      <div className='min-w-0'>
        <p className='truncate text-sm font-semibold text-foreground'>{label}</p>
        <p className='truncate text-xs text-muted-foreground'>
          {transferTargetNumber ? (
            <>
              <Trans id='features.calls.global.transferredTo'>Transferred to</Trans>{' '}
              {transferTargetNumber}
            </>
          ) : (
            <>
              {getCallName(call)} / {getPrimaryNumber(call)}
              {duration ? <span className='ml-2 font-medium tabular-nums'>{duration}</span> : null}
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function CallRow({
  call,
  selected,
  isOwnCall,
  isHeld,
  onSelect,
  onDismiss,
}: {
  call: CallCenterCallLog;
  selected: boolean;
  isOwnCall: boolean;
  isHeld: boolean;
  onSelect: () => void;
  onDismiss: () => void;
}) {
  const transferTargetNumber = getTransferTargetNumber(call);
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-2 py-2 ${
        selected ? 'border-brand-blue/50 bg-brand-blue/10' : 'border-border bg-muted/20'
      }`}
    >
      <button type='button' className='min-w-0 flex-1 text-left' onClick={onSelect}>
        <p className='truncate text-xs font-semibold text-foreground'>{getCallName(call)}</p>
        <p className='truncate text-xs text-muted-foreground'>
          {transferTargetNumber
            ? t({ id: 'features.calls.global.rowTransferredTo', message: 'Transferred to' })
            : isOwnCall
              ? isHeld
                ? t({ id: 'features.calls.global.rowHeld', message: 'Held' })
                : isOwnCall && call.direction === 'outbound'
                  ? (getOutboundPhaseLabel(call.status) ??
                    t({ id: 'features.calls.global.rowActive', message: 'Active' }))
                  : t({ id: 'features.calls.global.rowActive', message: 'Active' })
              : t({ id: 'features.calls.global.rowRinging', message: 'Ringing' })}
          {' / '}
          {transferTargetNumber ?? getPrimaryNumber(call)}
        </p>
      </button>
      <span
        className={`shrink-0 rounded-full px-2 py-1 text-xs font-extrabold tracking-wide ${
          isHeld
            ? 'bg-muted text-muted-foreground'
            : isOwnCall
              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200'
              : 'bg-brand-blue/10 text-brand-blue'
        }`}
      >
        {isHeld
          ? t({ id: 'features.calls.lineHeld', message: 'HELD' })
          : isOwnCall
            ? t({ id: 'features.calls.lineLive', message: 'LIVE' })
            : t({ id: 'features.calls.lineRinging', message: 'RINGING' })}
      </span>
      {!isOwnCall ? (
        <Button
          type='button'
          size='icon'
          variant='ghost'
          className='h-8 w-8'
          onClick={onDismiss}
          aria-label={t({
            id: 'features.calls.global.dismissIncoming',
            message: 'Dismiss incoming call',
          })}
        >
          <X className='h-4 w-4' />
        </Button>
      ) : null}
    </div>
  );
}

function IconAction({
  label,
  children,
  onClick,
  disabled,
  destructive = false,
  pressed,
}: {
  label: string;
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  pressed?: boolean;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      className={`flex h-14 flex-col items-center justify-center gap-1 rounded-xl border text-xs font-semibold transition active:scale-95 disabled:opacity-50 ${
        destructive
          ? 'border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15'
          : 'border-border bg-muted/30 text-foreground hover:bg-muted'
      }`}
    >
      {children}
      <span className='max-w-full truncate px-1'>{label}</span>
    </button>
  );
}

function GlobalCallDeviceControls({
  browserCall,
}: {
  browserCall: ReturnType<typeof useTelnyxWebRtcCall>;
}) {
  return (
    <div className='grid gap-2 rounded-xl border border-border/70 bg-muted/20 p-2'>
      {browserCall.permissionState === 'blocked' ? (
        <span className='text-xs font-medium text-destructive'>
          <Trans id='features.calls.microphoneBlocked'>Microphone blocked</Trans>
        </span>
      ) : null}
      {browserCall.audioBlocked ? (
        <div className='grid gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2'>
          <span className='text-xs font-medium text-amber-800 dark:text-amber-200'>
            <Trans id='features.calls.audioBlocked'>Call audio is blocked by this browser.</Trans>
          </span>
          <Button
            type='button'
            variant='outline'
            className='h-9 justify-center gap-2 border-amber-500/40 bg-background text-xs font-semibold'
            onClick={() => void browserCall.retryAudioPlayback()}
          >
            <Volume2 className='h-4 w-4' aria-hidden='true' />
            <Trans id='features.calls.enableAudio'>Enable audio</Trans>
          </Button>
        </div>
      ) : null}
      <div className='grid gap-2 sm:grid-cols-2'>
        <select
          aria-label={t({ id: 'features.calls.microphone', message: 'Microphone' })}
          value={browserCall.selectedMicrophoneId ?? ''}
          onChange={event => void browserCall.setMicrophone(event.target.value)}
          disabled={!browserCall.microphones.length}
          className='h-9 min-w-0 rounded-lg border border-border bg-background px-2 text-xs disabled:opacity-60'
        >
          <option value=''>
            {t({ id: 'features.calls.defaultMicrophone', message: 'Default microphone' })}
          </option>
          {browserCall.microphones.map(device => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label}
            </option>
          ))}
        </select>
        <div className='flex min-w-0 items-center gap-2'>
          <select
            aria-label={t({ id: 'features.calls.speaker', message: 'Speaker' })}
            value={browserCall.selectedSpeakerId ?? ''}
            onChange={event => void browserCall.setSpeaker(event.target.value)}
            disabled={!browserCall.speakers.length}
            className='h-9 min-w-0 flex-1 rounded-lg border border-border bg-background px-2 text-xs disabled:opacity-60'
          >
            <option value=''>
              {t({ id: 'features.calls.defaultSpeaker', message: 'Default speaker' })}
            </option>
            {browserCall.speakers.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label}
              </option>
            ))}
          </select>
          <Volume2 className='h-4 w-4 shrink-0 text-muted-foreground' aria-hidden='true' />
        </div>
      </div>
    </div>
  );
}

function TransferCallDialog({
  call,
  isPending,
  onClose,
  onTransfer,
}: {
  call: CallCenterCallLog | null;
  isPending: boolean;
  onClose: () => void;
  onTransfer: (toNumber: string) => Promise<void>;
}) {
  const [query, setQuery] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const contacts = useCallCenterContacts(query, {
    allowEmpty: true,
    enabled: Boolean(call),
    limit: 12,
    type: 'staff',
  });

  React.useEffect(() => {
    if (call) {
      setQuery('');
      setPhone('');
    }
  }, [call?.id]);

  const applyKey = (key: string) => {
    setPhone(previous => (key === 'backspace' ? previous.slice(0, -1) : `${previous}${key}`));
  };

  const manualNumber = normalizeTransferNumber(phone);

  const handleTransfer = async (target?: CallCenterContactDestination) => {
    const raw = target?.phoneNumber || phone;
    if (!raw.trim()) {
      toast.error(
        t({
          id: 'features.calls.transferTargetRequired',
          message: 'Choose a user or enter a phone number.',
        })
      );
      return;
    }
    const normalized = normalizeTransferNumber(raw);
    if (!normalized) {
      toast.error(
        t({
          id: 'features.calls.transferInvalidNumber',
          message: 'Enter a valid phone number before transferring.',
        })
      );
      return;
    }
    await onTransfer(normalized.e164);
  };

  return (
    <Dialog open={Boolean(call)} onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-w-[calc(100vw-1.25rem)] p-4 sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>
            <Trans id='features.calls.transferDialog.title'>Transfer call</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans id='features.calls.transferDialog.description'>
              Search admins and superadmins, or enter any phone number.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-3'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              className='pl-9'
              placeholder={t({
                id: 'features.calls.transferDialog.search',
                message: 'Search users',
              })}
            />
          </div>

          <div className='max-h-36 overflow-y-auto rounded-xl border border-border bg-muted/20 p-1'>
            {(contacts.data?.contacts ?? []).map(contact => (
              <button
                key={`${contact.type}:${contact.id}:${contact.phoneNumber}`}
                type='button'
                className='flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-background'
                onClick={() => {
                  setPhone(contact.phoneNumber);
                  void handleTransfer(contact);
                }}
                disabled={isPending}
              >
                <span className='min-w-0'>
                  <span className='block truncate text-sm font-semibold'>{contact.name}</span>
                  <span className='block truncate text-xs text-muted-foreground'>
                    {contact.roleBadge} / {contact.phoneNumber}
                  </span>
                </span>
                <Forward className='h-4 w-4 shrink-0 text-muted-foreground' />
              </button>
            ))}
            {!contacts.isLoading && !(contacts.data?.contacts ?? []).length ? (
              <p className='px-3 py-4 text-center text-xs text-muted-foreground'>
                <Trans id='features.calls.transferDialog.noUsers'>No matching staff users.</Trans>
              </p>
            ) : null}
          </div>

          <Input
            value={phone}
            onChange={event => setPhone(event.target.value)}
            inputMode='tel'
            placeholder='+15551234567'
            aria-label={t({
              id: 'features.calls.transferDialog.phone',
              message: 'Transfer phone number',
            })}
          />

          {phone.trim() ? (
            manualNumber ? (
              <p className='text-xs text-muted-foreground'>
                <Trans id='features.calls.transferDialog.willDial'>Will dial</Trans>{' '}
                <span className='font-semibold text-foreground'>{manualNumber.formatted}</span>
              </p>
            ) : (
              <p role='alert' className='text-xs font-medium text-destructive'>
                <Trans id='features.calls.transferDialog.invalidNumber'>
                  Enter a valid phone number.
                </Trans>
              </p>
            )
          ) : null}

          <div className='grid grid-cols-3 gap-2'>
            {keypadKeys.map(key => (
              <button
                key={key}
                type='button'
                onClick={() => applyKey(key)}
                className='flex h-11 items-center justify-center rounded-xl border border-border bg-muted/30 text-sm font-bold hover:bg-muted'
                aria-label={
                  key === 'backspace'
                    ? t({ id: 'features.calls.transferDialog.backspace', message: 'Backspace' })
                    : key
                }
              >
                {key === 'backspace' ? <Delete className='h-4 w-4' /> : key}
              </button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            <Trans id='common.cancel'>Cancel</Trans>
          </Button>
          <Button
            type='button'
            className='bg-brand-blue text-white hover:bg-brand-blue/90'
            onClick={() => void handleTransfer()}
            disabled={isPending || !manualNumber}
          >
            <Forward className='h-4 w-4' />
            <Trans id='features.calls.transfer'>Transfer</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
